import * as Sentry from "@sentry/nextjs";
import type {HttpsCallableResult} from "firebase/functions";
import {functions, httpsCallable} from "@/firebase/firebaseConfig";
import {logAnalyticsEvent} from "@/firebase/firebaseAnalytics";
import {getFirebaseAuthSetupFailureKey} from "@/lib/accountSetupFailure";
import {awaitFirstCallableResult, CALLABLE_ATTEMPT_TIMEOUT_MESSAGE} from "@/lib/callableAttemptRace";

const ENSURE_TIMEOUT_MS = 20000;
const ENSURE_MAX_ATTEMPTS = 3;
const ENSURE_BASE_BACKOFF_MS = 500;
const ENSURE_MAX_BACKOFF_MS = 4000;
const ENSURE_SUCCESS_TTL_MS = 60000;
const ENSURE_CALLABLE_NAME = "user-ensureUserProfile";

type EnsureFailureReason = "network" | "permission" | "callable_error" | "timeout" | "unknown";
type EnsureProviderType = "google" | "password" | "unknown";

export interface EnsureUserProfileResult {
    uid: string;
    profileCreated: boolean;
    identityBackfilled: boolean;
    updatedFields: string[];
    billingAccountId: string;
    emailVerified?: boolean;
    onboarding?: {
        pricing?: boolean;
        connect?: boolean;
        player?: boolean;
        editScreen?: boolean;
        intents?: Record<string, boolean>;
    };
    permissions?: {
        admin: boolean;
        betaProgram: boolean;
        dubbing: boolean;
        upload: boolean;
    };
    signupIntent?: string | null;
    pendingAuthReturnTo?: string | null;
    pendingAuthReturnToConsumed?: boolean;
    pendingAuthReturnToCreatedAtMs?: number | null;
    accountModel?: string | null;
    homeWorkspaceId?: string | null;
}

export interface EnsureUserProfileOptions {
    uid?: string;
    providerType?: string;
    source?: string;
    force?: boolean;
    /**
     * Gate pages that have already painted from the onboarding cache set this
     * to false. A timeout is still thrown and logged, but not captured: that
     * gate fail-opens and the page is already on screen. Other failure reasons
     * are still captured.
     */
    captureTimeouts?: boolean;
}

export class EnsureUserProfileError extends Error {
    reason: EnsureFailureReason;
    attemptCount: number;

    constructor(message: string, reason: EnsureFailureReason, attemptCount: number) {
        super(message);
        this.name = "EnsureUserProfileError";
        this.reason = reason;
        this.attemptCount = attemptCount;
    }
}

const inFlightEnsureByUid = new Map<string, Promise<EnsureUserProfileResult>>();
const recentEnsureSuccessByUid = new Map<string, {result: EnsureUserProfileResult; completedAt: number}>();

function normalizeProviderType(providerType?: string): EnsureProviderType {
    if (!providerType) return "unknown";
    if (providerType === "google.com" || providerType === "google") return "google";
    if (providerType === "password") return "password";
    return "unknown";
}

function getJitteredBackoffMs(attempt: number): number {
    const exponential = ENSURE_BASE_BACKOFF_MS * Math.pow(2, Math.max(0, attempt - 1));
    const withCap = Math.min(exponential, ENSURE_MAX_BACKOFF_MS);
    const jitter = Math.floor(Math.random() * 250);
    return withCap + jitter;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Timeouts on a background gate revalidation are expected when the callable
 * never settles. The route gate has already rendered, so capturing them opens
 * a Sentry error for a fail-open path. Permission and callable failures stay
 * captured even on that path.
 */
export function shouldCaptureEnsureFailure(input: {
    captureTimeouts?: boolean;
    reason: EnsureFailureReason;
}): boolean {
    if (input.reason === "timeout" && input.captureTimeouts === false) {
        return false;
    }
    return true;
}

function classifyEnsureFailure(error: any): EnsureFailureReason {
    const message = String(error?.message || "").toLowerCase();
    const code = String(error?.code || "").toLowerCase();

    if (message.includes(CALLABLE_ATTEMPT_TIMEOUT_MESSAGE)) return "timeout";
    if (code.includes("permission") || code.includes("unauthenticated")) return "permission";
    if (code.includes("unavailable") || code.includes("network") || message.includes("network")) return "network";
    if (code.includes("functions/") || code.includes("internal") || code.includes("invalid-argument")) return "callable_error";
    return "unknown";
}

function asIntentMap(value: unknown): Record<string, boolean> | undefined {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return undefined;
    }
    const intents: Record<string, boolean> = {};
    for (const [key, flag] of Object.entries(value as Record<string, unknown>)) {
        if (flag === true) {
            intents[key] = true;
        }
    }
    return intents;
}

export function parseEnsureUserProfileResult(data: any): EnsureUserProfileResult {
    const onboarding = data?.onboarding
        ? {
            pricing: Boolean(data.onboarding.pricing),
            connect: Boolean(data.onboarding.connect),
            player: Boolean(data.onboarding.player),
            editScreen: Boolean(data.onboarding.editScreen),
            intents: asIntentMap(data.onboarding.intents) || {},
        }
        : undefined;
    const permissions = data?.permissions
        ? {
            admin: Boolean(data.permissions.admin),
            betaProgram: data.permissions.betaProgram !== false,
            dubbing: data.permissions.dubbing !== false,
            upload: data.permissions.upload !== false,
        }
        : undefined;
    return {
        uid: String(data?.uid || ""),
        profileCreated: Boolean(data?.profileCreated),
        identityBackfilled: Boolean(data?.identityBackfilled),
        updatedFields: Array.isArray(data?.updatedFields) ? data.updatedFields.map((field: unknown) => String(field)) : [],
        billingAccountId: String(data?.billingAccountId || ""),
        emailVerified: typeof data?.emailVerified === "boolean" ? data.emailVerified : undefined,
        onboarding,
        permissions,
        signupIntent: typeof data?.signupIntent === "string" ? data.signupIntent : null,
        pendingAuthReturnTo: typeof data?.pendingAuthReturnTo === "string" ? data.pendingAuthReturnTo : null,
        pendingAuthReturnToConsumed: Boolean(data?.pendingAuthReturnToConsumed),
        pendingAuthReturnToCreatedAtMs: typeof data?.pendingAuthReturnToCreatedAtMs === "number"
            ? data.pendingAuthReturnToCreatedAtMs
            : null,
        accountModel: typeof data?.accountModel === "string" && data.accountModel.trim()
            ? data.accountModel.trim()
            : null,
        homeWorkspaceId: typeof data?.homeWorkspaceId === "string" && data.homeWorkspaceId.trim()
            ? data.homeWorkspaceId.trim()
            : null,
    };
}

export function getEnsureFailureKey(error: unknown): string {
    if (error instanceof EnsureUserProfileError) {
        if (error.reason === "timeout") {
            return "errors.accountSetupTimeout";
        }
        if (error.reason === "permission") {
            return "errors.accountSetupPermission";
        }
        if (error.reason === "network") {
            return "errors.accountSetupNetwork";
        }
    }
    return getFirebaseAuthSetupFailureKey(error) ?? "errors.accountSetupGeneric";
}

/** @deprecated Use getEnsureFailureKey with useTranslations */
export function getEnsureFailureMessage(error: unknown): string {
    return getEnsureFailureKey(error);
}

async function runEnsureAttempt(
    options: EnsureUserProfileOptions,
    inFlight: Set<Promise<HttpsCallableResult>>,
): Promise<EnsureUserProfileResult> {
    const ensureFn = httpsCallable(functions, ENSURE_CALLABLE_NAME);
    const response = await awaitFirstCallableResult(inFlight, ensureFn({
        source: options.source || "unknown",
        providerType: options.providerType || "unknown",
    }), ENSURE_TIMEOUT_MS);
    return parseEnsureUserProfileResult(response.data);
}

async function executeEnsureUserProfile(options: EnsureUserProfileOptions): Promise<EnsureUserProfileResult> {
    const provider = normalizeProviderType(options.providerType);
    const source = options.source || "unknown";
    const uid = options.uid || "unknown";
    const startedAt = Date.now();
    let lastError: unknown = null;
    let lastReason: EnsureFailureReason = "unknown";
    const inFlightCalls = new Set<Promise<HttpsCallableResult>>();

    void logAnalyticsEvent("user_profile_ensure_start", {
        uid,
        provider_type: provider,
        source,
    });

    for (let attempt = 1; attempt <= ENSURE_MAX_ATTEMPTS; attempt++) {
        try {
            Sentry.addBreadcrumb({
                category: "auth.callable",
                message: "Ensuring user profile",
                level: "info",
                data: {
                    callable_name: ENSURE_CALLABLE_NAME,
                    uid,
                    provider_type: provider,
                    source,
                    attempt,
                },
            });
            const result = await runEnsureAttempt(options, inFlightCalls);
            const timeToReadyMs = Date.now() - startedAt;

            void logAnalyticsEvent("user_profile_ensure_success", {
                uid: result.uid || uid,
                provider_type: provider,
                source,
                attempt_count: attempt,
                time_to_ready_ms: timeToReadyMs,
                profile_created: result.profileCreated,
                identity_backfilled: result.identityBackfilled,
                updated_fields_count: result.updatedFields.length,
            });

            return result;
        } catch (error) {
            lastError = error;
            lastReason = classifyEnsureFailure(error);

            Sentry.addBreadcrumb({
                category: "auth.callable",
                message: "Ensure user profile attempt failed",
                level: "warning",
                data: {
                    callable_name: ENSURE_CALLABLE_NAME,
                    uid,
                    provider_type: provider,
                    source,
                    attempt,
                    ensure_failure_reason: lastReason,
                    error_code: String((error as { code?: string })?.code || ""),
                    error_message: String((error as { message?: string })?.message || ""),
                },
            });

            if (attempt < ENSURE_MAX_ATTEMPTS) {
                const backoffMs = getJitteredBackoffMs(attempt);
                void logAnalyticsEvent("user_profile_ensure_retry", {
                    uid,
                    provider_type: provider,
                    source,
                    attempt_count: attempt,
                    ensure_failure_reason: lastReason,
                    backoff_ms: backoffMs,
                });
                await sleep(backoffMs);
            }
        }
    }

    const durationMs = Date.now() - startedAt;
    void logAnalyticsEvent("user_profile_ensure_failure", {
        uid,
        provider_type: provider,
        source,
        attempt_count: ENSURE_MAX_ATTEMPTS,
        ensure_failure_reason: lastReason,
        duration_ms: durationMs,
    });

    if (shouldCaptureEnsureFailure({
        captureTimeouts: options.captureTimeouts,
        reason: lastReason,
    })) {
        Sentry.captureException(lastError instanceof Error ? lastError : new Error("Ensure user profile failed"), {
            tags: {
                component: "ensure-user-profile",
                provider_type: provider,
                source,
                ensure_failure_reason: lastReason,
                callable_name: ENSURE_CALLABLE_NAME,
            },
            user: {
                id: uid || "unknown",
            },
            extra: {
                attempt_count: ENSURE_MAX_ATTEMPTS,
                duration_ms: durationMs,
                callable_name: ENSURE_CALLABLE_NAME,
            },
        });
    }

    throw new EnsureUserProfileError(
        "Failed to ensure user profile",
        lastReason,
        ENSURE_MAX_ATTEMPTS
    );
}

export function ensureUserProfile(options: EnsureUserProfileOptions = {}): Promise<EnsureUserProfileResult> {
    const cacheKey = options.uid || "unknown";
    const now = Date.now();
    const recentSuccess = recentEnsureSuccessByUid.get(cacheKey);
    if (!options.force && recentSuccess && (now - recentSuccess.completedAt) <= ENSURE_SUCCESS_TTL_MS) {
        return Promise.resolve(recentSuccess.result);
    }

    const inFlight = inFlightEnsureByUid.get(cacheKey);
    if (inFlight) {
        return inFlight;
    }

    const promise = executeEnsureUserProfile(options)
        .then((result) => {
            recentEnsureSuccessByUid.set(cacheKey, {
                result,
                completedAt: Date.now(),
            });
            return result;
        })
        .finally(() => {
            inFlightEnsureByUid.delete(cacheKey);
        });

    inFlightEnsureByUid.set(cacheKey, promise);
    return promise;
}

export function clearEnsureUserProfileCache(uid?: string): void {
    if (uid) {
        recentEnsureSuccessByUid.delete(uid);
        inFlightEnsureByUid.delete(uid);
        return;
    }
    recentEnsureSuccessByUid.clear();
    inFlightEnsureByUid.clear();
}

export function rememberEnsureOnboarding(
    uid: string,
    patch: NonNullable<EnsureUserProfileResult["onboarding"]>
): void {
    const existing = recentEnsureSuccessByUid.get(uid);
    if (!existing) {
        return;
    }
    recentEnsureSuccessByUid.set(uid, {
        ...existing,
        result: {
            ...existing.result,
            onboarding: {
                pricing: patch.pricing ?? existing.result.onboarding?.pricing,
                connect: patch.connect ?? existing.result.onboarding?.connect,
                player: patch.player ?? existing.result.onboarding?.player,
                editScreen: patch.editScreen ?? existing.result.onboarding?.editScreen,
                intents: patch.intents ?? existing.result.onboarding?.intents,
            },
        },
    });
}
